import {LinePlotDisplayFnArgs} from '../types';
import {LinePlotTemplateSettings} from '../workspaceTemplatesQueries';

// Helper types to extract keys by their value type
type KeysOfType<T, V> = {
  [K in keyof T]-?: T[K] extends V | undefined ? K : never;
}[keyof T];

// Keys for each specific type
type BooleanKeys = KeysOfType<LinePlotTemplateSettings, boolean>;
type NumberKeys = KeysOfType<LinePlotTemplateSettings, number>;
type EnumKeys = Exclude<
  keyof LinePlotTemplateSettings,
  BooleanKeys | NumberKeys
>;

type DisplayConfig<K extends keyof LinePlotTemplateSettings> = {
  field: K;
  label: string;
  groupTitle?: string;
  defaultValue?: LinePlotTemplateSettings[K];
};

type OptionalFormatterConfig<K extends keyof LinePlotTemplateSettings> =
  DisplayConfig<K> & {
    formatter?: (value: NonNullable<LinePlotTemplateSettings[K]>) => string;
  };

const getDisplayValue = <K extends keyof LinePlotTemplateSettings>(
  value: LinePlotTemplateSettings[K],
  formatter?: (value: NonNullable<LinePlotTemplateSettings[K]>) => string
): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (formatter) {
    return formatter(value as NonNullable<LinePlotTemplateSettings[K]>);
  }
  return String(value);
};

/**
 * Factory for creating display functions for boolean fields.
 * Handles fields that display as "true" or "false" strings.
 *
 * @example
 * const getShowLegendDisplayRow = createBooleanDisplayFn({
 *   field: 'showLegend',
 *   label: 'Show legend',
 *   defaultValue: DEFAULT_LINE_PLOT_SETTINGS.showLegend,
 * });
 */
export const createBooleanDisplayFn = <K extends BooleanKeys>(
  config: OptionalFormatterConfig<K>
) => {
  return ({
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
  }: LinePlotDisplayFnArgs) => {
    const currValue = currTemplateLinePlotSettings?.[config.field] as
      | boolean
      | undefined;
    const newValue =
      (currWorkspaceLinePlotSettings?.[config.field] as boolean | undefined) ??
      config.defaultValue;

    return {
      ...(config.groupTitle ? {groupTitle: config.groupTitle} : {}),
      label: config.label,
      field: config.field,
      currValue,
      currDisplayValue: getDisplayValue(currValue, config.formatter),
      newValue,
      newDisplayValue: getDisplayValue(newValue, config.formatter),
    };
  };
};

/**
 * Factory for creating display functions for number fields.
 * Handles fields that display as number strings.
 * If defaultValue is provided, it will be used when the workspace value is undefined.
 * If defaultValue is not provided, undefined values remain undefined (optional fields).
 *
 * @example
 * // With default value
 * const getMaxRunsDisplayRow = createNumberDisplayFn({
 *   field: 'maxRuns',
 *   label: 'Max runs',
 *   groupTitle: 'Max number of runs',
 *   defaultValue: DEFAULT_LINE_PLOT_SETTINGS.limit,
 * });
 *
 * @example
 * // Optional field (no default)
 * const getXAxisMinDisplayRow = createNumberDisplayFn({
 *   field: 'xAxisMin',
 *   label: 'X-axis min',
 * });
 */
export const createNumberDisplayFn = <K extends NumberKeys>(
  config: DisplayConfig<K>
) => {
  return ({
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
  }: LinePlotDisplayFnArgs) => {
    const currValue = currTemplateLinePlotSettings?.[config.field] as
      | number
      | undefined;
    const workspaceValue = currWorkspaceLinePlotSettings?.[config.field] as
      | number
      | undefined;

    // Use workspace value, or default if provided, otherwise undefined
    let newValue: number | undefined;
    if (workspaceValue !== undefined) {
      newValue = workspaceValue;
    } else if (config.defaultValue !== undefined) {
      newValue = config.defaultValue as number;
    } else {
      newValue = undefined;
    }

    return {
      ...(config.groupTitle ? {groupTitle: config.groupTitle} : {}),
      label: config.label,
      field: config.field,
      currValue,
      currDisplayValue: currValue == null ? undefined : String(currValue),
      newValue,
      newDisplayValue: newValue == null ? undefined : String(newValue),
    };
  };
};

/**
 * Factory for creating display functions for enum fields with custom formatters.
 * Handles fields that need special display formatting (e.g., "mean" -> "Mean").
 *
 * @example
 * const getGroupAggDisplayRow = createEnumDisplayFn({
 *   field: 'groupAgg',
 *   label: 'Group aggregation',
 *   groupTitle: 'Grouping',
 *   defaultValue: DEFAULT_LINE_PLOT_SETTINGS.groupAgg,
 *   formatter: (value) => capitalizeFirst(value),
 * });
 */
export const createEnumDisplayFn = <K extends EnumKeys>(
  config: OptionalFormatterConfig<K>
) => {
  return ({
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
  }: LinePlotDisplayFnArgs) => {
    const currValue = currTemplateLinePlotSettings?.[config.field];
    const newValue =
      currWorkspaceLinePlotSettings?.[config.field] ?? config.defaultValue;

    return {
      ...(config.groupTitle ? {groupTitle: config.groupTitle} : {}),
      label: config.label,
      field: config.field,
      currValue,
      currDisplayValue: getDisplayValue(currValue, config.formatter),
      newValue,
      newDisplayValue: getDisplayValue(newValue, config.formatter),
    };
  };
};
